import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Upload,
  Modal,
  message,
  Typography,
  Tag,
  Space,
  Empty,
  Image,
  Spin,
  Form,
  InputNumber,
  Popconfirm,
  Table,
} from "antd";

import {
  SearchOutlined,
  UploadOutlined,
  EnvironmentOutlined,
  CarOutlined,
  PictureOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import apiClient from "@/utils/apiClient";

const { Title, Text } = Typography;

const API_BASE =
  import.meta.env.VITE_API_BASE_URL;

const resolveImageUrl = (image) => {
  if (!image) {
    return "";
  }

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  if (image.startsWith("/uploads")) {
    return `${window.location.origin}${image}`;
  }

  if (image.startsWith("uploads")) {
    return `${window.location.origin}/${image}`;
  }

  return image;
};

const TransportManagementNew = () => {

  const [routes, setRoutes] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [uploading, setUploading] =
    useState(false);

  const [selectedRoute, setSelectedRoute] =
    useState(null);

  const [imageModal, setImageModal] =
    useState(false);

const [serviceModal, setServiceModal] =
  useState(false);

const [saving, setSaving] =
  useState(false);

const [form] =
  Form.useForm();

  // =====================================================
  // FETCH ROUTES
  // =====================================================

  const fetchRoutes =
    async () => {

      try {

        setLoading(true);

        const res =
          await apiClient.get(
            "/transport-plan/inventory"
          );

        setRoutes(
          res?.data?.data || []
        );

      } catch (error) {

        console.error(
          "Fetch routes error:",
          error
        );

        message.error(
          "Failed to fetch routes"
        );

      } finally {

        setLoading(false);
      }
    };

  useEffect(() => {
    fetchRoutes();
  }, []);

  // =====================================================
  // SEARCH
  // =====================================================

  const filteredRoutes =
    useMemo(() => {

      return routes.filter(
        (route) => {

          const q =
            search.toLowerCase();

          return (
            route.source
              ?.toLowerCase()
              .includes(q) ||

            route.destination
              ?.toLowerCase()
              .includes(q) ||

            route.routeName
              ?.toLowerCase()
              .includes(q) ||

            route.serviceType
              ?.toLowerCase()
              .includes(q)
          );
        }
      );
    }, [routes, search]);

  // =====================================================
  // IMAGE UPLOAD
  // =====================================================

  const handleImageUpload =
    async (file) => {

      try {

        if (!selectedRoute) {
          return;
        }

        setUploading(true);

        const formData =
          new FormData();

        formData.append(
          "image",
          file
        );

        const uploadRes =
          await apiClient.post(
            "/transport-plan/upload-image",
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        const imageUrl =
          uploadRes?.data?.data
            ?.imageUrl;

        if (!imageUrl) {
          throw new Error(
            "Upload failed"
          );
        }

await apiClient.put(
  `/transport-plan/inventory/${selectedRoute._id}`,
  {
    image: imageUrl,
  }
);

        message.success(
          "Route image updated"
        );

        setImageModal(false);

        fetchRoutes();

        return false;

      } catch (error) {

        console.error(
          "Route image upload error:",
          error
        );

        message.error(
          "Failed to upload image"
        );

        return Upload.LIST_IGNORE;

      } finally {

        setUploading(false);
      }
    };

// =====================================================
// CREATE SERVICE
// =====================================================

const handleCreateService =
  async (values) => {

    try {

      setSaving(true);

      const payload = {

        source:
          values.source,

        destination:
          values.destination,

        routeName:
          `${values.source} → ${values.destination}`,

        serviceType:
          values.serviceType,

        distance:
          values.distance || 0,

        duration:
          values.duration || "",

        daySchedule:
          values.daySchedule || "",


        vehiclePricing: [
          {
            vehicleType:
              values.vehicleType,

            price:
               Number(values.price || 0),
          },
        ],
      };

      await apiClient.post(
        "/transport-plan/inventory",
        payload
      );

      message.success(
        "Transport service created"
      );

      setServiceModal(false);

      form.resetFields();

      fetchRoutes();

    } catch (error) {

      console.error(
        "Create service error:",
        error
      );

      message.error(
        "Failed to create service"
      );

    } finally {

      setSaving(false);
    }
  };

  // =====================================================
  // CSV IMPORT
  // =====================================================

  const uploadProps = {

    name: "file",

    action:
      `${API_BASE}/transport-plan/import`,

    headers: {
      authorization:
        `Bearer ${localStorage.getItem("token")}`,
    },

    onChange(info) {

      if (
        info.file.status ===
        "done"
      ) {

        message.success(
          "Routes imported successfully"
        );

        fetchRoutes();
      }

      if (
        info.file.status ===
        "error"
      ) {

        message.error(
          "Import failed"
        );
      }
    },
  };

// =====================================================
// DELETE ROUTE
// =====================================================

const handleDeleteRoute =
  async (routeId) => {

    try {

      await apiClient.delete(
        `/transport-plan/inventory/${routeId}`
      );

      message.success(
        "Route deleted successfully"
      );

      fetchRoutes();

    } catch (error) {

      console.error(
        "Delete route error:",
        error
      );

      message.error(
        "Failed to delete route"
      );
    }
  };

  // =====================================================
  // UI
  // =====================================================

  return (

    <div
      style={{
        padding: 24,
      }}
    >

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          marginBottom: 24,
        }}
      >

        <div>

          <Title
            level={2}
            style={{
              margin: 0,
            }}
          >

            <CarOutlined
              style={{
                marginRight: 10,
                color: "#1677ff",
              }}
            />

            Transport Inventory

          </Title>

          <Text type="secondary">

            Route inventory for quotations and operations

          </Text>

        </div>

<Space>

  <Button
    type="primary"
    icon={
      <PlusOutlined />
    }
    onClick={() =>
      setServiceModal(true)
    }
  >
    Add New Service
  </Button>

  <Upload
    {...uploadProps}
    showUploadList={false}
  >

    <Button
      icon={
        <UploadOutlined />
      }
    >
      Import CSV
    </Button>

  </Upload>

</Space>

      </div>

      {/* =====================================================
          SEARCH
      ===================================================== */}

      <Card
        style={{
          marginBottom: 24,
          borderRadius: 20,
        }}
      >

        <Input
          size="large"
          placeholder="Search by source, destination or service..."
          prefix={
            <SearchOutlined />
          }
          value={search}
          onChange={(e) =>
            setSearch(
              e.target.value
            )
          }
        />

      </Card>

      {/* =====================================================
          ROUTE CARDS
      ===================================================== */}

      {loading ? (

        <div
          style={{
            textAlign: "center",
            padding: 100,
          }}
        >

          <Spin size="large" />

        </div>

      ) : filteredRoutes.length === 0 ? (

        <Empty
          description="No transport routes found"
        />

      ) : (


<Table
  rowKey="_id"
  dataSource={filteredRoutes}
  pagination={{
    pageSize: 20,
  }}
  scroll={{ x: 1200 }}
  columns={[
    {
      title: "Route",
      render: (_, route) => (
        <Space
          direction="vertical"
          size={0}
        >
          <Text strong>
            {route.source}
            {" → "}
            {route.destination}
          </Text>

          <Tag color="blue">
            {route.serviceType ||
              "Transport"}
          </Tag>
        </Space>
      ),
    },

    {
      title: "Distance",
      dataIndex: "distance",
      render: (v) =>
        `${v || 0} km`,
    },

    {
      title: "Duration",
      dataIndex: "duration",
      render: (v) => v || "-",
    },

    {
      title: "Vehicles",
      render: (_, route) => (
        <Space
          direction="vertical"
          size={0}
        >
          {(route.vehiclePricing || []).map(
            (v, idx) => (
              <Text key={idx}>
                {v.vehicleType}
                {" : ₹"}
                {v.price}
              </Text>
            )
          )}
        </Space>
      ),
    },

{
  title: "Image",
  render: (_, route) => (

    route.image ? (

      <Image
        preview={false}
        src={resolveImageUrl(route.image)}
        width={90}
        height={60}
        style={{
          objectFit: "cover",
          borderRadius: 8,
        }}
      />

    ) : (

      <Text type="secondary">
        No Image
      </Text>

    )
  ),
},

{
  title: "Actions",

      render: (_, route) => (
        <Space>

          <Button
            size="small"
            icon={
              <UploadOutlined />
            }
            onClick={() => {

              setSelectedRoute(
                route
              );

              setImageModal(
                true
              );
            }}
          >

            Upload Image

          </Button>

          <Popconfirm
            title="Delete this route?"
            onConfirm={() =>
              handleDeleteRoute(
                route._id
              )
            }
          >

            <Button
              danger
              size="small"
              icon={
                <DeleteOutlined />
              }
            >

              Delete

            </Button>

          </Popconfirm>

        </Space>
      ),
    },
  ]}
/>

      )}

{/* =====================================================
    CREATE SERVICE MODAL
===================================================== */}

<Modal
  open={serviceModal}
  footer={null}
  onCancel={() =>
    setServiceModal(false)
  }
  title="Add Transport Service"
  width={700}
>

  <Form
    form={form}
    layout="vertical"
    onFinish={
      handleCreateService
    }
  >

    <Row gutter={16}>

      <Col span={12}>

        <Form.Item
          name="source"
          label="Source"
          rules={[
            {
              required: true,
            },
          ]}
        >

          <Input />

        </Form.Item>

      </Col>

      <Col span={12}>

        <Form.Item
          name="destination"
          label="Destination"
          rules={[
            {
              required: true,
            },
          ]}
        >

          <Input />

        </Form.Item>

      </Col>

    </Row>

    <Row gutter={16}>

      <Col span={12}>

        <Form.Item
          name="serviceType"
          label="Service Type"
        >

          <Input />

        </Form.Item>

      </Col>

      <Col span={12}>

<Form.Item
  name="vehicleType"
  label="Vehicle Type"
  rules={[
    {
      required: true,
      message: "Vehicle type required",
    },
  ]}
>

  <Input
    placeholder="Example: Innova / Crysta / Tempo"
  />

</Form.Item>

      </Col>

    </Row>

    <Row gutter={16}>

      <Col span={12}>

        <Form.Item
          name="price"
          label="Price"
        >

          <InputNumber
            style={{
              width: "100%",
            }}
          />

        </Form.Item>

      </Col>

      <Col span={12}>

        <Form.Item
          name="distance"
          label="Distance"
        >

          <InputNumber
            style={{
              width: "100%",
            }}
          />

        </Form.Item>

      </Col>

    </Row>

    <Form.Item
      name="duration"
      label="Duration"
    >

      <Input />

    </Form.Item>

    <Form.Item
      name="daySchedule"
      label="Day Schedule"
    >

      <Input.TextArea
        rows={4}
      />

    </Form.Item>

    <Button
      type="primary"
      htmlType="submit"
      loading={saving}
      block
    >
      Create Service
    </Button>

  </Form>

</Modal>

      {/* =====================================================
          IMAGE MODAL
      ===================================================== */}

      <Modal
        open={imageModal}
        footer={null}
        onCancel={() =>
          setImageModal(false)
        }
        title="Upload Route Image"
      >

        <Upload
          beforeUpload={
            handleImageUpload
          }
          showUploadList={false}
        >

          <Button
            loading={uploading}
            icon={
              <UploadOutlined />
            }
          >
            Select Image
          </Button>

        </Upload>

      </Modal>

    </div>
  );
};

export default TransportManagementNew;
